'use client';

import Link from 'next/link';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { capturePosthogEvent } from '@/lib/posthog';
import { buildCrossDomainQuizUrl } from '@/lib/posthog-cross-domain';
import StripeButton from './StripeButton';
import Button from '@/components/ui/Button';

const BrandCubeMark = dynamic(() => import('./BrandCubeMark'), {
  ssr: false,
  loading: () => <div className="w-11 h-11" />,
});

// Each nav link points at a feature-marketing page that explains the
// surface and ends in a CTA, rather than dropping the visitor straight
// into the quiz / group host / report sample. On tryvotely.com these
// pages render the marketing content; their CTAs cross-link absolute
// to votelyquiz.com so the marketing host never serves the product.
const NAV_LINKS = [
  { label: 'The Test', href: '/test' },
  { label: 'Ideologies', href: '/ideologies' },
  { label: 'Group Quiz', href: '/group-quiz' },
  { label: 'Report', href: '/report' },
];

export default function Header() {
  const posthog = usePostHog();
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Off the home page, prepend an explicit Home link. Usability feedback:
  // visitors on the four feature pages didn't know the logo goes home and
  // felt lost, so give them a labeled way back.
  const navLinks = pathname === '/' ? NAV_LINKS : [{ label: 'Home', href: '/' }, ...NAV_LINKS];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
      const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setMobileOpen(false); };
      window.addEventListener('keydown', onEsc);
      return () => { document.body.style.overflow = ''; window.removeEventListener('keydown', onEsc); };
    }
  }, [mobileOpen]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 font-inter ${
        mobileOpen
          ? 'bg-surface border-b border-border'
          : scrolled
            ? 'bg-surface/95 backdrop-blur-sm border-b border-border shadow-card'
            : 'bg-transparent'
      }`}
    >
      <div className="mx-auto flex items-center justify-between px-5 lg:px-8 py-3.5" style={{ maxWidth: 'var(--max-w-landing)' }}>
        {/* Logo */}
        <div className="flex items-center gap-2.5">
          <BrandCubeMark size={44} />
          <Link href="/" className="text-3xl font-semibold text-text tracking-tight">Votely</Link>
        </div>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              onClick={() => capturePosthogEvent(posthog, 'nav_link_clicked', { destination: link.href })}
              className="text-base font-medium text-text-secondary hover:text-text transition-colors"
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Desktop CTA - routes to /start splash (stars + two quiz buttons + FAQ). */}
        <StripeButton
          href="/start"
          className="hidden md:inline-flex"
          onClick={(e) => {
            capturePosthogEvent(posthog, 'cta_start_clicked', { location: 'header' }, { sendToServer: true });
            const crossUrl = buildCrossDomainQuizUrl('/start', posthog);
            if (crossUrl !== '/start') {
              e.preventDefault();
              window.location.assign(crossUrl);
            }
          }}
        >
          Start quiz
        </StripeButton>

        {/* Mobile menu button */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden p-2.5 -mr-0.5 text-text"
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-x-0 top-[72px] h-[calc(100dvh-72px)] overflow-y-auto bg-surface z-40">
          <nav className="flex flex-col p-6 gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => {
                  setMobileOpen(false);
                  capturePosthogEvent(posthog, 'nav_link_clicked', { destination: link.href });
                }}
                className="py-3 px-4 text-base font-medium text-text-secondary hover:text-text hover:bg-cta-secondary rounded-lg transition-colors"
              >
                {link.label}
              </Link>
            ))}
            <Button
              href="/start"
              onClick={(e) => {
                setMobileOpen(false);
                capturePosthogEvent(posthog, 'cta_start_clicked', { location: 'header_mobile' }, { sendToServer: true });
                const crossUrl = buildCrossDomainQuizUrl('/start', posthog);
                if (crossUrl !== '/start') {
                  e.preventDefault();
                  window.location.assign(crossUrl);
                }
              }}
              className="mt-4 px-5"
            >
              Start quiz
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
}
